/**
 * Custom hook to check if user's profile is complete
 */

import { useState, useEffect } from "react";
import { useAuth } from "../contexts/AuthContext";
import { isProfileComplete } from "../utils/profileCompletion";
import userManagementApi from "../services/user-management-api";
import { Employee } from "../types/leave";

interface UseProfileCompletionReturn {
  isComplete: boolean;
  isLoading: boolean;
  error: string | null;
  profile: Employee | null;
}

/**
 * Hook to check if the current user's profile is complete
 * Fetches the user's profile from the backend and checks if all required fields are filled
 * @returns Object containing isComplete status, loading state, error, and profile data
 */
export const useProfileCompletion = (): UseProfileCompletionReturn => {
  const { user } = useAuth();
  const [isComplete, setIsComplete] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [profile, setProfile] = useState<Employee | null>(null);

  useEffect(() => {
    const checkProfileCompletion = async () => {
      if (!user?.id) {
        // Keep loading true while waiting for user to be loaded
        // Don't set isLoading to false here - let AuthContext finish loading first
        return;
      }

      try {
        setIsLoading(true);
        setError(null);

        // Fetch the user's full profile
        const userProfile = await userManagementApi.getMyProfile();
        setProfile(userProfile);

        // Check if profile is complete
        const complete = isProfileComplete(userProfile);
        setIsComplete(complete);
      } catch (err) {
        const errorMessage =
          err instanceof Error
            ? err.message
            : "Failed to check profile completion";
        setError(errorMessage);
        setIsComplete(false);
      } finally {
        setIsLoading(false);
      }
    };

    checkProfileCompletion();
  }, [user?.id]);

  return { isComplete, isLoading, error, profile };
};
